import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, X, Loader2, MessageCircle, Pencil, Download, Upload } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

export default function Contacts() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/contacts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === rows.length ? new Set() : new Set(rows.map((c) => c.contact_id)));

  const exportCsv = async () => {
    try {
      const r = await api.get("/contacts-export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "contacts.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/contacts-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.added} · skipped ${r.data.skipped_existing} existing · ${r.data.invalid_rows} invalid`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} contact(s)? This also removes their conversations and leads.`)) return;
    try {
      await api.post("/contacts-bulk-delete", { contact_ids: ids });
      toast.success(`Deleted ${ids.length} contact(s)`);
      setSelected(new Set()); load();
    } catch { toast.error("Bulk delete failed"); }
  };

  const openAdd = () => { setEditId(null); setForm({ name: "", phone: "", email: "" }); setShowAdd(true); };
  const openEdit = (c) => { setEditId(c.contact_id); setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "" }); setShowAdd(true); };

  const submit = async () => {
    if (!editId && !form.phone.trim()) { toast.error("Phone number is required"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/contacts/${editId}`, { name: form.name, email: form.email });
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", form);
        toast.success("Contact added");
      }
      setShowAdd(false); setEditId(null); setForm({ name: "", phone: "", email: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save contact"); }
    finally { setSaving(false); }
  };

  const message = async (id) => {
    try {
      const r = await api.post(`/contacts/${id}/conversation`);
      navigate(`/inbox?conversation=${r.data.conversation_id}`);
    } catch (e) { toast.error("Could not open conversation"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Remove ${name}? This also deletes their conversations and lead.`)) return;
    try { await api.delete(`/contacts/${id}`); toast.success("Contact removed"); load(); }
    catch (e) { toast.error("Failed to remove"); }
  };

  return (
    <>
      <PageHeader eyebrow="CRM" title="Contacts"
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="export-contacts-button" onClick={exportCsv}
              className="flex items-center gap-2 border border-[#E9EDEF] bg-white text-[#111B21] hover:bg-[#F0F2F5] rounded-lg font-medium px-3 py-2 text-sm transition-colors">
              <Download className="h-4 w-4" /> Export
            </button>
            <label data-testid="import-contacts-button"
              className="flex items-center gap-2 border border-[#E9EDEF] bg-white text-[#111B21] hover:bg-[#F0F2F5] rounded-lg font-medium px-3 py-2 text-sm transition-colors cursor-pointer">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
              <input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="import-contacts-input" />
            </label>
            <button data-testid="add-contact-button" onClick={openAdd}
              className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors">
              <UserPlus className="h-4 w-4" /> Add Contact
            </button>
          </div>
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8">
        {selected.size > 0 && (
          <div className="mb-3 flex items-center justify-between bg-[#FEF2F2] border border-red-200 rounded-lg px-4 py-2" data-testid="bulk-bar">
            <span className="text-sm text-red-700">{selected.size} selected</span>
            <button data-testid="bulk-delete-button" onClick={bulkDelete}
              className="flex items-center gap-1.5 bg-red-600 text-white hover:bg-red-700 rounded-lg px-3 py-1.5 text-sm font-medium">
              <Trash2 className="h-4 w-4" /> Delete selected
            </button>
          </div>
        )}
        <div className="border border-[#E9EDEF] bg-white rounded-lg overflow-x-auto">
          <table className="w-full text-left" data-testid="contacts-table">
            <thead>
              <tr className="border-b border-[#E9EDEF] font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">
                <th className="px-4 py-3"><input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} data-testid="select-all-contacts" /></th>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Phone</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 font-medium">Lead Score</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E9EDEF]">
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-[#8696A0]">
                  <Users className="h-5 w-5 mx-auto mb-2 text-[#54656F]" />No contacts yet.
                </td></tr>
              )}
              {rows.map((c) => (
                <tr key={c.contact_id} className="hover:bg-[#F0F2F5] transition-colors">
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.has(c.contact_id)} onChange={() => toggleSelect(c.contact_id)} data-testid={`select-contact-${c.contact_id}`} /></td>
                  <td className="px-5 py-3 text-sm text-[#111B21]">{c.name}</td>
                  <td className="px-5 py-3 text-sm text-[#54656F] font-mono">{c.phone}</td>
                  <td className="px-5 py-3 text-sm text-[#8696A0]">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-sm text-[#8696A0]">{c.source}</td>
                  <td className="px-5 py-3 text-sm font-mono text-[#075E54]">{c.lead_score ?? "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`message-contact-${c.contact_id}`} onClick={() => message(c.contact_id)} title="Message on WhatsApp"
                        className="text-[#8696A0] hover:text-green-600 transition-colors"><MessageCircle className="h-4 w-4" /></button>
                      <button data-testid={`edit-contact-${c.contact_id}`} onClick={() => openEdit(c)} title="Edit"
                        className="text-[#8696A0] hover:text-[#075E54] transition-colors"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-contact-${c.contact_id}`} onClick={() => del(c.contact_id, c.name)} title="Remove"
                        className="text-[#8696A0] hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-contact-modal">
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
          <div className="relative w-full max-w-md bg-[#F0F2F5] border border-[#E9EDEF] rounded-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E9EDEF]">
              <h3 className="font-heading text-sm font-medium text-[#111B21]">{editId ? "Edit Contact" : "Add Contact"}</h3>
              <button onClick={() => setShowAdd(false)} className="text-[#8696A0] hover:text-[#111B21]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Name</label>
                <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls + " mt-1"} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Phone (with country code)</label>
                <input data-testid="contact-phone" value={form.phone} disabled={!!editId}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91XXXXXXXXXX" className={inputCls + " mt-1 font-mono disabled:opacity-60"} />
                {editId && <p className="text-[10px] text-[#8696A0] mt-1">Phone can't be changed (it identifies the WhatsApp contact).</p>}
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Email (optional)</label>
                <input data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" className={inputCls + " mt-1"} />
              </div>
              <button data-testid="save-contact" onClick={submit} disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} {editId ? "Save Changes" : "Add Contact"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
